import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from 'reactstrap';
import DataTable from 'react-data-table-component';
import notify, { MESSAGES } from '../../../utils/notify';
import classnames from 'classnames';
import useImprovementClarificationList, {
  buildImprovementClarificationListUrl,
} from '../hooks/useImprovementClarificationList';
import useIcListProcurementMitraMap from '../hooks/useIcListProcurementMitraMap';
import { unwrapImprovementClarificationList } from '../utils/improvementClarificationHelpers';
import {
  CLARIFICATION_STATUS_OPTIONS,
  IMPROVEMENT_STATUS_OPTIONS,
  exportImprovementClarificationList,
  getClarificationTone,
  getDetailState,
  getImprovementTone,
} from '../utils/improvementClarificationList';
import { CLARIFICATION_PROGRESS } from '../../../shared/offer-domain/clarification';
import { useClarificationUnreadByProcurement } from '../../../shared/offer-domain/respond';
import { getAuthToken, isAuthenticated } from '../../../auth/tokenvalidation';
import ActionButton from '../../../Components/Common/ActionButton';
import { EmptyState } from '../../../Components/Common/ListStates';
import {
  ActiveFilterChips,
  ClearFiltersButton,
  ListFilterSelect,
  ListSearch,
} from '../../../Components/Common/ListFilters';
import { fetchJson } from '../../Procurement/utils/procurementList';
import { ProductAvatar } from '../../Procurement/component/ListBadges';
import '../../TenderOpening/components/TenderOpening.css';
import '../../Procurement/ProcurementView.css';
import '../ImprovementClarification.css';
import StatusBadge from '../../../Components/Common/StatusBadge';
import { getListProductTypeLabel } from '../../../shared/offer-domain/constants';

const isWaitingResponseFromDmut = row => {
  const key = Number(row?.statusClarificationProgress);
  if (key === CLARIFICATION_PROGRESS.WAITING_DMUT) return true;
  const label = String(row?.statusClarification ?? '')
    .trim()
    .toLowerCase();
  return (
    label === 'waiting response from registered supplier' ||
    label === 'waiting respond from registered supplier'
  );
};

const StatusPill = ({ wording, tone }) => {
  if (!wording) return <span className='text-muted'>-</span>;

  return <StatusBadge tone={tone} label={wording} title={wording} />;
};

const getProductName = item =>
  item?.productType?.product?.name || item?.productType?.name;

const ProductInfo = ({ item }) => {
  const name = getProductName(item);
  const sub = getListProductTypeLabel(item, item?.productType?.name);
  if (!name && !sub) return null;

  return (
    <div className='to-product'>
      <span className='to-product-name'>{name || '-'}</span>
      {sub && <span className='text-truncate ms-2'>· {sub}</span>}
    </div>
  );
};

const SkeletonRows = ({ count = 5 }) => (
  <div className='w-100 px-3 py-2'>
    {Array.from({ length: count }).map((_, index) => (
      <div key={index} className='d-flex align-items-center gap-3 py-3'>
        <div className='to-skeleton' style={{ width: '9%' }} />
        <div className='to-skeleton' style={{ width: '26%' }} />
        <div className='to-skeleton' style={{ width: '12%' }} />
        <div className='to-skeleton' style={{ width: '18%' }} />
        <div className='to-skeleton flex-grow-1' />
      </div>
    ))}
  </div>
);

// Waiting on the registered supplier, or unread messages: highlighted as before.
const buildRowStyles = unreadByProcurementId => [
  {
    when: isWaitingResponseFromDmut,
    classNames: ['table-warning'],
    style: { backgroundColor: 'var(--vz-warning-bg-subtle, #fff3cd)' },
  },
  {
    when: row =>
      !isWaitingResponseFromDmut(row) &&
      Boolean(unreadByProcurementId[String(row.id)]),
    classNames: ['table-info'],
    style: { backgroundColor: 'var(--vz-info-bg-subtle, #cff4fc)' },
  },
];

const SORT_FIELD_TO_COLUMN = {
  procurementNumber: 'idProc',
  titleProcurement: 'title',
  procurementType: 'procType',
  statusImprovementProgressLabel: 'statusImprovement',
  statusClarificationProgressLabel: 'statusClarification',
};

const ImprovementClarificationTable = ({
  procurementTypeHeader,
  refreshTrigger = 0,
  exportName = 'List',
}) => {
  const navigate = useNavigate();
  const [isExporting, setIsExporting] = useState(false);

  const {
    data,
    totalRows,
    currentPage,
    rowsPerPage,
    searchText,
    debouncedSearchText,
    statusImprovementFilter,
    statusClarificationFilter,
    sortField,
    sortDirection,
    isLoading,
    startEntry,
    endEntry,
    handleSearch,
    handleStatusImprovementFilter,
    handleStatusClarificationFilter,
    handlePageChange,
    handlePerRowsChange,
    handleSort,
    clearFilters,
  } = useImprovementClarificationList({
    procurementTypeHeader,
    refreshTrigger,
  });

  const procurementIds = useMemo(
    () => (data?.items ?? []).map(item => item.id).filter(Boolean),
    [data?.items]
  );
  const seedMitraByProcurementId = useMemo(() => {
    const map = {};
    (data?.items ?? []).forEach(item => {
      if (item?.id && item?.procurementMitraId) {
        map[item.id] = item.procurementMitraId;
      }
    });
    return map;
  }, [data?.items]);
  const procurementMitraByProcurementId = useIcListProcurementMitraMap({
    procurementIds,
    seedMitraByProcurementId,
    enabled: procurementIds.length > 0,
  }).resolvedMap;
  const { unreadByProcurementId } = useClarificationUnreadByProcurement({
    procurementIds,
    procurementMitraByProcurementId,
    isEvaluatorView: false,
    enabled: procurementIds.length > 0,
    refreshKey: refreshTrigger,
  });

  // Exports every row matching the current filters, not just this page.
  const handleExport = async format => {
    const token = getAuthToken();
    if (!token || !isAuthenticated()) {
      notify.error(MESSAGES.authRequired);
      return;
    }

    setIsExporting(true);
    try {
      const query = {
        currentPage: 1,
        procurementTypeHeader,
        search: debouncedSearchText,
        statusImprovement: statusImprovementFilter,
        statusClarification: statusClarificationFilter,
        sortField,
        sortDirection,
      };
      let result = unwrapImprovementClarificationList(
        await fetchJson(
          buildImprovementClarificationListUrl({
            ...query,
            rowsPerPage: Math.max(totalRows, 1),
          }),
          token
        )
      );
      if (result.totalItems > result.items.length) {
        result = unwrapImprovementClarificationList(
          await fetchJson(
            buildImprovementClarificationListUrl({
              ...query,
              rowsPerPage: result.totalItems,
            }),
            token
          )
        );
      }

      if (!result.items.length) {
        notify.info(MESSAGES.noDataToExport);
        return;
      }
      exportImprovementClarificationList(result.items, {
        format,
        sheetName: exportName,
      });
    } catch (error) {
      notify.apiError(error, 'export improvement clarification data');
    } finally {
      setIsExporting(false);
    }
  };

  const items = data.items || [];
  const totalPages = Math.max(1, Math.ceil(totalRows / rowsPerPage));
  const filterChips = [
    searchText && {
      key: 'search',
      label: 'Search',
      value: searchText,
      onRemove: () => handleSearch({ target: { value: '' } }),
    },
    statusImprovementFilter && {
      key: 'improvement',
      label: 'Improvement',
      value: statusImprovementFilter,
      onRemove: () => handleStatusImprovementFilter(''),
    },
    statusClarificationFilter && {
      key: 'clarification',
      label: 'Clarification',
      value: statusClarificationFilter,
      onRemove: () => handleStatusClarificationFilter(''),
    },
  ].filter(Boolean);
  const hasFilters = filterChips.length > 0;
  const goToDetail = row =>
    navigate(`/apps-improvementclarification/dtl/${row.id}`, {
      state: getDetailState(row),
    });
  const isUnread = row => Boolean(unreadByProcurementId[String(row.id)]);

  const emptyState = (
    <EmptyState
      title='No improvement clarification found'
      emptyHint='Requests appear here when the tender committee asks for an improvement or clarification on your offer during tender evaluation.'
      filteredHint='Nothing matches the current search or filters.'
      hasFilters={hasFilters}
      onClear={clearFilters}
    />
  );

  const columns = [
    {
      id: 'idProc',
      name: 'ID Proc',
      selector: row => row.idProc,
      cell: row => (
        <span className={classnames('to-id', { 'text-info': isUnread(row) })}>
          {row.idProc || '-'}
        </span>
      ),
      width: '10%',
      sortable: true,
    },
    {
      id: 'title',
      name: 'Procurement',
      selector: row => row.title,
      cell: row => (
        <div className='d-flex align-items-center gap-2 py-2 w-100'>
          <ProductAvatar name={getProductName(row)} />
          <div className='pv-text-col flex-grow-1'>
            <div className='to-title' title={row.title}>
              {row.title || '-'}
            </div>
            <ProductInfo item={row} />
          </div>
        </div>
      ),
      width: '25%',
      sortable: true,
    },
    {
      id: 'procType',
      name: 'Proc Type',
      selector: row => row.procType,
      cell: row => <span className='to-tag'>{row.procType || '-'}</span>,
      width: '12%',
      sortable: true,
    },
    {
      id: 'productType',
      name: 'Product Type',
      selector: row => getProductName(row),
      cell: row => (
        <span className='to-product-name'>{getProductName(row) || '-'}</span>
      ),
      width: '10%',
      sortable: true,
    },
    {
      id: 'description',
      name: 'Description',
      selector: row => row.description,
      cell: row => (
        <span className='pv-desc' title={row.description || ''}>
          {row.description || '-'}
        </span>
      ),
      width: '12%',
      sortable: true,
    },
    {
      id: 'statusImprovement',
      name: 'Improvement',
      selector: row => row.statusImprovement,
      cell: row => (
        <StatusPill
          wording={row.statusImprovement}
          tone={getImprovementTone(row.statusImprovement)}
        />
      ),
      width: '10%',
      sortable: true,
    },
    {
      id: 'statusClarification',
      name: 'Clarification',
      selector: row => row.statusClarification,
      cell: row => (
        <StatusPill
          wording={row.statusClarification}
          tone={getClarificationTone(row.statusClarification)}
        />
      ),
      width: '15%',
      sortable: true,
    },
    {
      name: <span className='text-center w-100'>Action</span>,
      cell: row => (
        <div className='d-flex gap-1 justify-content-center w-100'>
          <ActionButton
            variant='icon'
            size='sm'
            icon='ri-arrow-right-up-line'
            label='Open detail'
            onClick={e => {
              e.stopPropagation();
              goToDetail(row);
            }}
          />
        </div>
      ),
      width: '6%',
      center: true,
    },
  ];

  const MobileCard = ({ item }) => (
    <div
      className={classnames(
        'to-mcard d-md-none',
        `to-mcard--${getImprovementTone(item.statusImprovement)}`,
        { 'ic-mcard-unread': isUnread(item) }
      )}
    >
      <div className='d-flex justify-content-between align-items-center gap-2 mb-2'>
        <span className={classnames('to-id', { 'text-info': isUnread(item) })}>
          {item.idProc || '-'}
        </span>
      </div>

      <div className='d-flex align-items-center gap-2'>
        <ProductAvatar name={getProductName(item)} />
        <div className='pv-text-col flex-grow-1'>
          <div className='to-title'>{item.title || '-'}</div>
          <ProductInfo item={item} />
        </div>
      </div>

      <div className='to-mcard-meta'>
        <div>
          <span>Proc Type</span>
          <span>{item.procType || '-'}</span>
        </div>
        <div>
          <span>Improvement</span>
          <StatusPill
            wording={item.statusImprovement}
            tone={getImprovementTone(item.statusImprovement)}
          />
        </div>
        <div>
          <span>Clarification</span>
          <StatusPill
            wording={item.statusClarification}
            tone={getClarificationTone(item.statusClarification)}
          />
        </div>
      </div>

      {item.description && (
        <p className='pv-desc mb-3' title={item.description}>
          {item.description}
        </p>
      )}

      <div className='d-flex gap-2'>
        <ActionButton
          variant='secondary'
          size='sm'
          icon='ri-eye-line'
          className='flex-fill'
          onClick={() => goToDetail(item)}
        >
          View Detail
        </ActionButton>
      </div>
    </div>
  );

  return (
    <div className='to-panel'>
      <div className='to-toolbar'>
        <ListSearch
          value={searchText}
          onChange={handleSearch}
          placeholder='Search by title or procurement no...'
          ariaLabel='Search improvement clarification'
        />
        <ListFilterSelect
          label='Improvement'
          value={statusImprovementFilter}
          onChange={handleStatusImprovementFilter}
          allLabel='All Improvement'
          options={IMPROVEMENT_STATUS_OPTIONS.map(status => ({
            value: status,
            label: status,
          }))}
        />
        <ListFilterSelect
          label='Clarification'
          value={statusClarificationFilter}
          onChange={handleStatusClarificationFilter}
          allLabel='All Clarification'
          options={CLARIFICATION_STATUS_OPTIONS.map(status => ({
            value: status,
            label: status,
          }))}
        />
        <ClearFiltersButton count={filterChips.length} onClick={clearFilters} />
        <span className='to-toolbar-meta'>
          {isLoading
            ? 'Loading...'
            : `Showing ${startEntry}–${endEntry} of ${totalRows} procurements`}
        </span>
        <UncontrolledDropdown>
          <DropdownToggle
            tag='button'
            type='button'
            className='btn btn-light btn-sm d-inline-flex align-items-center'
            disabled={isExporting || isLoading || !totalRows}
          >
            {isExporting ? (
              <span
                className='spinner-border spinner-border-sm me-2'
                role='status'
                aria-hidden='true'
              />
            ) : (
              <i className='ri-download-2-line me-2' aria-hidden='true' />
            )}
            Export
          </DropdownToggle>
          <DropdownMenu end>
            <DropdownItem onClick={() => handleExport('xlsx')}>
              <i className='ri-file-excel-2-line align-middle me-2 text-success'></i>
              Export to Excel
            </DropdownItem>
            <DropdownItem onClick={() => handleExport('csv')}>
              <i className='ri-file-text-line align-middle me-2 text-muted'></i>
              Export to CSV
            </DropdownItem>
          </DropdownMenu>
        </UncontrolledDropdown>
      </div>

      <ActiveFilterChips chips={filterChips} />

      <div className='d-md-none p-3'>
        {isLoading && <SkeletonRows count={3} />}
        {!isLoading &&
          items.map((item, index) => (
            <MobileCard key={item.id || index} item={item} />
          ))}
        {!isLoading && !items.length && emptyState}
      </div>

      <div className='d-none d-md-block to-table-wrap'>
        <DataTable
          key={`sort-${sortField}-${sortDirection}`}
          columns={columns}
          data={items}
          pagination={false}
          onSort={handleSort}
          sortServer
          defaultSortFieldId={SORT_FIELD_TO_COLUMN[sortField] || sortField}
          defaultSortAsc={sortDirection === 'asc'}
          onRowClicked={goToDetail}
          conditionalRowStyles={buildRowStyles(unreadByProcurementId)}
          highlightOnHover
          pointerOnHover
          persistTableHead
          progressPending={isLoading}
          progressComponent={<SkeletonRows />}
          noDataComponent={emptyState}
          customStyles={{
            headCells: {
              style: { paddingLeft: '12px', paddingRight: '12px' },
            },
            cells: {
              style: {
                paddingLeft: '12px',
                paddingRight: '12px',
                wordBreak: 'break-word',
              },
            },
            table: { style: { width: '100%' } },
            tableWrapper: { style: { display: 'table' } },
          }}
        />
      </div>

      <div className='to-footer'>
        <div className='d-flex align-items-center gap-2'>
          <span>Rows per page</span>
          <select
            className='form-select form-select-sm'
            style={{ width: 'auto' }}
            value={rowsPerPage}
            onChange={e => handlePerRowsChange(parseInt(e.target.value, 10))}
            aria-label='Rows per page'
          >
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
          <span className='to-footer-range'>
            {startEntry}–{endEntry} of <strong>{totalRows}</strong> items
          </span>
        </div>

        <div className='to-pager'>
          <button
            type='button'
            className='to-pager-btn'
            disabled={currentPage <= 1 || isLoading}
            onClick={() => handlePageChange(1)}
            aria-label='First page'
            title='First page'
          >
            <i className='ri-skip-back-line' aria-hidden='true' />
          </button>
          <button
            type='button'
            className='to-pager-btn'
            disabled={currentPage <= 1 || isLoading}
            onClick={() => handlePageChange(currentPage - 1)}
            aria-label='Previous page'
            title='Previous page'
          >
            <i className='ri-arrow-left-s-line' aria-hidden='true' />
          </button>
          <span className='to-pager-current'>
            Page {currentPage} of {totalPages}
          </span>
          <button
            type='button'
            className='to-pager-btn'
            disabled={currentPage >= totalPages || isLoading}
            onClick={() => handlePageChange(currentPage + 1)}
            aria-label='Next page'
            title='Next page'
          >
            <i className='ri-arrow-right-s-line' aria-hidden='true' />
          </button>
          <button
            type='button'
            className='to-pager-btn'
            disabled={currentPage >= totalPages || isLoading}
            onClick={() => handlePageChange(totalPages)}
            aria-label='Last page'
            title='Last page'
          >
            <i className='ri-skip-forward-line' aria-hidden='true' />
          </button>
        </div>
      </div>
    </div>
  );
};

export default React.memo(ImprovementClarificationTable);
